export const AGE_GROUP_KEY = "playmath.ageGroup";
export const AGE_GROUP_CHANGED_EVENT = "playmath:ageGroupChanged";

/** Cookie mirror of `playmath.ageGroup` so middleware can block /contact for kids. */
export const AGE_GROUP_COOKIE = AGE_GROUP_KEY;

export type AgeGroup = "kid" | "grownup";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

export function getAgeGroupFromStorage(): AgeGroup | null {
  try {
    if (typeof window === "undefined") return null;
    const value = window.localStorage.getItem(AGE_GROUP_KEY);
    if (value === "kid" || value === "grownup") return value;
    return null;
  } catch {
    return null;
  }
}

export function isGrownupFromStorage(): boolean {
  return getAgeGroupFromStorage() === "grownup";
}

export function notifyAgeGroupChanged(ageGroup: AgeGroup): void {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(AGE_GROUP_CHANGED_EVENT, {
        detail: { ageGroup },
      })
    );
  } catch {
    // Non-critical; used only for client reactivity
  }
}

function setAgeGroupCookie(ageGroup: AgeGroup): void {
  try {
    if (typeof document === "undefined") return;
    const secure =
      typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${AGE_GROUP_COOKIE}=${ageGroup}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Non-critical; localStorage remains the client source of truth.
  }
}

export function setAgeGroupInStorage(ageGroup: AgeGroup, opts?: { notify?: boolean }): void {
  const { notify = true } = opts ?? {};

  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(AGE_GROUP_KEY, ageGroup);
  } catch {
    // Non-critical; if storage is unavailable, we can't persist the choice.
    return;
  }

  setAgeGroupCookie(ageGroup);

  if (notify) {
    notifyAgeGroupChanged(ageGroup);
  }
}
